import type {
  ArenaChallengeStatus,
  ArenaGroup,
  ArenaGroupRole,
  ArenaOpenChallenge,
  ArenaPaymentMode,
  ArenaPlayerSearchResult,
  ArenaPost,
  ArenaReactionType,
  ArenaRematchDraftPayload,
  BookingPaymentFlow,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { ARENA_FUNCTION_NAME } from "./_core/function-names";

export const arenaCommunityService = {
  async listOpenChallenges(filters?: {
    sport_id?: string;
    city?: string;
    skill_level?: string;
    status?: ArenaChallengeStatus | "all";
  }): Promise<ArenaOpenChallenge[]> {
    return callEdgeData<ArenaOpenChallenge[]>(ARENA_FUNCTION_NAME, "list-open-challenges", {
      method: "GET",
      params: {
        ...(filters?.sport_id ? { sport_id: filters.sport_id } : {}),
        ...(filters?.city ? { city: filters.city } : {}),
        ...(filters?.skill_level ? { skill_level: filters.skill_level } : {}),
        ...(filters?.status ? { status: filters.status } : {}),
      },
    });
  },

  async listArenaFeed(filters?: {
    group_id?: string;
    limit?: number;
  }): Promise<ArenaPost[]> {
    return callEdgeData<ArenaPost[]>(ARENA_FUNCTION_NAME, "list-arena-feed", {
      method: "GET",
      params: {
        ...(filters?.group_id ? { group_id: filters.group_id } : {}),
        ...(filters?.limit ? { limit: String(filters.limit) } : {}),
      },
    });
  },

  async createArenaPost(data: {
    group_id?: string;
    message?: string;
    media_url?: string | null;
    media_path?: string | null;
    visibility?: "arena" | "group";
  }): Promise<ArenaPost> {
    return callEdgeData<ArenaPost>(ARENA_FUNCTION_NAME, "create-arena-post", {
      body: data,
    });
  },

  async deleteArenaPost(data: { post_id: string }): Promise<{ success: boolean; post_id: string }> {
    return callEdgeData<{ success: boolean; post_id: string }>(ARENA_FUNCTION_NAME, "delete-arena-post", {
      body: data,
    });
  },

  async createArenaComment(data: {
    post_id: string;
    message: string;
  }): Promise<{ success: boolean; post_id: string }> {
    return callEdgeData<{ success: boolean; post_id: string }>(ARENA_FUNCTION_NAME, "create-arena-comment", {
      body: data,
    });
  },

  async deleteArenaComment(data: {
    comment_id: string;
  }): Promise<{ success: boolean; comment_id: string; post_id: string }> {
    return callEdgeData<{ success: boolean; comment_id: string; post_id: string }>(
      ARENA_FUNCTION_NAME,
      "delete-arena-comment",
      {
        body: data,
      },
    );
  },

  async toggleArenaReaction(data: {
    post_id: string;
    reaction_type: ArenaReactionType;
  }): Promise<{ success: boolean; post_id: string; reaction_type: ArenaReactionType | null }> {
    return callEdgeData<{ success: boolean; post_id: string; reaction_type: ArenaReactionType | null }>(
      ARENA_FUNCTION_NAME,
      "toggle-arena-reaction",
      {
        body: data,
      },
    );
  },

  async createOpenChallenge(data: {
    group_id?: string;
    title?: string;
    court_id: string;
    sport_id?: string;
    start_at: string;
    end_at: string;
    skill_level?: string;
    visibility?: "public" | "private" | "invite_only";
    notes?: string;
    payment_flow_choice?: BookingPaymentFlow;
    arena_payment_mode?: ArenaPaymentMode;
    creator_payment_method?: "captain" | "rateio";
    expires_at?: string;
    accepted_group_id?: string;
    rematch_of_match_id?: string;
  }): Promise<ArenaOpenChallenge> {
    return callEdgeData<ArenaOpenChallenge>(ARENA_FUNCTION_NAME, "create-open-challenge", {
      body: data,
    });
  },

  async acceptOpenChallenge(data: {
    arena_open_challenge_id: string;
    group_id?: string;
    message?: string;
    team_payment_method?: "captain" | "rateio";
  }): Promise<ArenaOpenChallenge> {
    return callEdgeData<ArenaOpenChallenge>(ARENA_FUNCTION_NAME, "accept-open-challenge", {
      body: data,
    });
  },

  async cancelOpenChallenge(data: {
    arena_open_challenge_id: string;
    reason?: string;
  }): Promise<{ success: boolean; challenge: ArenaOpenChallenge }> {
    return callEdgeData<{ success: boolean; challenge: ArenaOpenChallenge }>(
      ARENA_FUNCTION_NAME,
      "cancel-open-challenge",
      {
        body: data,
      },
    );
  },

  async remindOpenChallenge(data: {
    arena_open_challenge_id: string;
  }): Promise<ArenaOpenChallenge> {
    return callEdgeData<ArenaOpenChallenge>(ARENA_FUNCTION_NAME, "remind-open-challenge", {
      body: data,
    });
  },

  async requestRematch(data: {
    arena_open_challenge_id: string;
    group_id?: string;
  }): Promise<{ challenge: ArenaOpenChallenge; rematch_draft: ArenaRematchDraftPayload }> {
    return callEdgeData<{ challenge: ArenaOpenChallenge; rematch_draft: ArenaRematchDraftPayload }>(
      ARENA_FUNCTION_NAME,
      "request-rematch",
      {
        body: data,
      },
    );
  },

  async listMyGroups(): Promise<ArenaGroup[]> {
    return callEdgeData<ArenaGroup[]>(ARENA_FUNCTION_NAME, "list-my-groups", {
      method: "GET",
    });
  },

  async createGroup(data: {
    name: string;
    sport_id?: string;
    city?: string;
    description?: string;
    avatar_url?: string;
    cover_url?: string;
    emblem_key?: string;
    is_public?: boolean;
    max_members?: number;
  }): Promise<ArenaGroup> {
    return callEdgeData<ArenaGroup>(ARENA_FUNCTION_NAME, "create-group", {
      body: data,
    });
  },

  async updateGroupIdentity(data: {
    group_id: string;
    name?: string;
    sport_id?: string | null;
    city?: string | null;
    description?: string | null;
    avatar_url?: string | null;
    avatar_path?: string | null;
    cover_url?: string | null;
    cover_path?: string | null;
    emblem_key?: string | null;
    is_public?: boolean;
    max_members?: number | null;
  }): Promise<ArenaGroup> {
    return callEdgeData<ArenaGroup>(ARENA_FUNCTION_NAME, "update-group-identity", {
      body: data,
    });
  },

  async searchPlusPlayers(data: { q: string }): Promise<ArenaPlayerSearchResult[]> {
    return callEdgeData<ArenaPlayerSearchResult[]>(ARENA_FUNCTION_NAME, "search-plus-players", {
      method: "GET",
      params: { q: data.q },
    });
  },

  async inviteGroupMember(data: {
    group_id: string;
    user_id?: string;
    email?: string;
    role?: ArenaGroupRole;
  }): Promise<{ group_id: string; membership_id: string; invited_user_id: string }> {
    return callEdgeData<{ group_id: string; membership_id: string; invited_user_id: string }>(
      ARENA_FUNCTION_NAME,
      "invite-group-member",
      {
        body: data,
      },
    );
  },

  async respondGroupInvite(data: {
    group_id: string;
    accept: boolean;
  }): Promise<{ success: boolean; group_id: string; accepted: boolean }> {
    return callEdgeData<{ success: boolean; group_id: string; accepted: boolean }>(
      ARENA_FUNCTION_NAME,
      "respond-group-invite",
      { body: data },
    );
  },

  async setActiveGroup(data: { group_id: string }): Promise<{ success: boolean; group_id: string }> {
    return callEdgeData<{ success: boolean; group_id: string }>(ARENA_FUNCTION_NAME, "set-active-group", {
      body: data,
    });
  },

  async listMyChallenges(): Promise<ArenaOpenChallenge[]> {
    return callEdgeData<ArenaOpenChallenge[]>(ARENA_FUNCTION_NAME, "list-my-challenges", {
      method: "GET",
    });
  },
};
